import { Drawer, type PaperProps } from "@mui/material";
import { memo, ReactNode, useEffect, useRef } from "react";
import {
  RedoButtonCloseX,
  ButtonSize as XButtonSize,
} from "../redo-button-close-x";

/**
 * @param onModalCloseRequested - Called when x clicked, the user presses escape, or clicks away
 * @param open - Controls the drawer visibility with slide-in/slide-out animation (default: true)
 * @param showCloseButton - Whether to show the X close button in the top right (default: false)
 */
export interface RedoDrawerProps {
  children?: ReactNode | undefined;
  onModalCloseRequested(): void;
  open?: boolean;
  PaperProps?: Partial<PaperProps>;
  ref?: React.RefObject<HTMLElement>;
  showCloseButton?: boolean;
  uiSelector?: string;
}

export const RedoDrawer = memo(function RedoDrawer({
  children,
  onModalCloseRequested,
  open = true,
  PaperProps: paperProps,
  showCloseButton = false,
  uiSelector,
}: RedoDrawerProps) {
  const lastChildrenRef = useRef<ReactNode>(children);

  useEffect(() => {
    if (children) {
      lastChildrenRef.current = children;
    }
  }, [children]);

  const displayChildren = children ?? lastChildrenRef.current;

  const dataUi = uiSelector ?? "portal-modal";
  return (
    <Drawer
      anchor="right"
      // Marker for the outer modal root (which contains the backdrop). The
      // Paper inside carries `data-ui={dataUi}` so customer-portal CSS rules
      // that paint the modal surface only affect the visible panel, not the
      // full-viewport modal root.
      data-ui-root={dataUi}
      onClose={onModalCloseRequested}
      open={open}
      PaperProps={{ ...paperProps, ...({ "data-ui": dataUi } as object) }}
    >
      {showCloseButton && (
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1,
          }}
        >
          <RedoButtonCloseX
            onclick={onModalCloseRequested}
            onDarkBackground={false}
            size={XButtonSize.XSMALL}
          />
        </div>
      )}
      {displayChildren}
    </Drawer>
  );
});
